import React from "react";
import { useAviator } from "../../store/aviator";
import { playSound } from "../../utils";
import { useSocket } from "../../providers/SocketProvider";

interface CashoutSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  cashoutData: {
    cashOutAtMultiplier: string;
    cashout: number;
    stake: number;
  };
  offset?: number;
}

const CashoutSuccessModal: React.FC<CashoutSuccessModalProps> = ({
  isOpen,
  onClose,
  cashoutData,
  offset = 0,
}) => {
  const { aviatorState } = useAviator();
  const { currencyConfig } = useSocket();
  const onCloseRef = React.useRef(onClose);

  React.useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Auto-close after 3 seconds
  React.useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      onCloseRef.current();
    }, 3000);

    return () => clearTimeout(timer);
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen || !aviatorState.fxChecked) return;
    playSound("win");
  }, [isOpen, aviatorState.fxChecked]);

  if (!isOpen) return null;

  const { cashOutAtMultiplier, cashout, stake } = cashoutData;
  const winAmount = cashout - stake;

  return (
    <div
      className="fixed left-0 right-0 z-50 flex justify-center animate-fadeIn px-4"
      style={{ top: 16 + offset * 80 }}
    >
      <div
        className="relative animate-scaleIn w-[90%] max-w-[25rem]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Main Banner - Single Dark Green Capsule */}
        <div className="bg-[#004a00] rounded-full border border-[#28A909] shadow-2xl p-1 relative">
          {/* Grid Layout: 45% Cashout | 45% Win | 10% Close Button */}
          <div className="grid grid-cols-[45%_45%_10%] items-center gap-2">
            {/* Left Section - Cashout Text (45%) */}
            <div className="text-white text-center">
              <div className="text-xs font-normal mb-1 text-gray-100">
                You have cashed out!
              </div>
              <div className="text-2xl font-medium text-white">
                {cashOutAtMultiplier}x
              </div>
            </div>

            {/* Middle Section - Win Inner Capsule (45%) */}
            <div className="bg-[#28A909] rounded-full px-3 py-1 relative w-full">
              <div className="text-xs font-medium text-white text-center">
                Win {currencyConfig.code}
              </div>
              <div className="text-lg font-bold text-white text-center">
                {winAmount.toFixed(2)}
              </div>

              {/* Decorative Stars - Inside the capsule */}
              <div className="absolute top-1 left-2 animate-pulse">
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#4ade80"
                  strokeWidth="2"
                >
                  <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                </svg>
              </div>
              <div
                className="absolute bottom-1 left-2 animate-pulse"
                style={{ animationDelay: "0.5s" }}
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#4ade80"
                  strokeWidth="2"
                >
                  <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                </svg>
              </div>
            </div>

            {/* Right Section - Close Button (10%) */}
            <div className="flex justify-center">
              <button
                onClick={onClose}
                className="w-6 h-6 flex items-center justify-center text-white hover:text-gray-300 transition-colors"
              >
                <span className="text-lg font-bold text-gray-400">×</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashoutSuccessModal;
